import {ReactNode, useEffect} from 'react';
import {OnlyInLoginChecker} from '../../../../common/checkers/OnlyInLoginChecker';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import {MainTab} from '../../../../design-system/organisms/MainTab/MainTab';
import {SaleMethod} from '../../../../graphql/generated/types';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {ReactQueryFetcher} from '../../../../reactQuery/ReactQueryFetcher';
import {useContentHomeOverviewQuery} from '../../../../wrapper/contentWrapper';
import {
  useContentCheckFreeTicketQuery,
  useContentMyTicketQuery,
} from '../../../../wrapper/ticketWrapper';
import {REFRESH_MY_TICKET_EVENT} from '../constant';
import {ContentFreeTicketCheckerHeadless} from '../headless/ContentFreeTicketCheckerHeadless';
import {ContentSaleStateCheckerHeadless} from '../headless/ContentSaleStateCheckerHeadless';
import {useContentHomeTabList} from '../hook/useContentHomeTabList';
import {setIsEnteredContentHome} from '../state/contentHomeSlice';
import {ContentHomeTabType} from '../type';
import {ContentDetailTabContainer} from './ContentDetailTabContainer';
import {ContentHomeTabInfoContainer} from './ContentHomeTabInfoContainer';
import {ContentHomeTabPcContainer} from './ContentHomeTabPcContainer';
import {ContentMainTicketActionContainer} from './ContentMainTicketActionContainer';
import {ContentNoticeTabPcContainer} from './ContentNoticeTabPcContainer';
import {ContentOverviewContainer} from './ContentOverviewContainer';
import {ContentReceiveFreeTicketContainer} from './ContentReceiveFreeTicketContainer';

export interface ContentMainPcContainerProps {
  seriesId: number;
  tabType: ContentHomeTabType;
}

export function ContentMainPcContainer({
  seriesId,
  tabType,
}: ContentMainPcContainerProps) {
  const dispatch = useTypedDispatch();
  const tabList = useContentHomeTabList({seriesId});
  const data = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG).data;
  const isCashTypeHome =
    data?.contentHomeOverview?.content?.saleMethod === SaleMethod.Cash;

  const {refetch: refetchMyTicket} = useContentMyTicketQuery(
    {
      seriesId,
      includeWaitfree: true,
    },
    {enabled: false}
  );

  useEffect(() => {
    dispatch(setIsEnteredContentHome({seriesId, isEntered: true}));
    return () => {
      dispatch(setIsEnteredContentHome({seriesId, isEntered: false}));
    };
  }, [dispatch, seriesId]);

  useEffect(() => {
    const refetch = () => refetchMyTicket();

    window.addEventListener(REFRESH_MY_TICKET_EVENT, refetch);
    return () => {
      window.removeEventListener(REFRESH_MY_TICKET_EVENT, refetch);
    };
  }, [refetchMyTicket]);

  return (
    <div className="flex flex-row flex-1">
      <div className="flex flex-col mb-28pxr w-320pxr">
        <div className="rounded-t-12pxr bg-theme-bg-surface-alt">
          <ContentOverviewContainer seriesId={seriesId} />
          <ContentSaleStateCheckerHeadless seriesId={seriesId}>
            <OnlyInLoginChecker>
              {!!data && !isCashTypeHome && (
                <>
                  <ReactQueryFetcher
                    reactQuery={useContentCheckFreeTicketQuery}
                    variables={{seriesId}}
                    ignoreLoading={true}
                    ignoreFetching={true}
                    options={{throwOnError: false}}
                    // free_check API 는 옵셔널한 API 라 어떤 에러가 발생하든 무시합니다. (ios, aos 동일)
                    onError={() => {}}
                  >
                    {({contentCheckFreeTicket}) => (
                      <ContentFreeTicketCheckerHeadless
                        seriesId={seriesId}
                        contentCheckFreeTicket={contentCheckFreeTicket}
                      >
                        <ContentReceiveFreeTicketContainer
                          seriesId={seriesId}
                          contentCheckFreeTicket={contentCheckFreeTicket}
                        />
                      </ContentFreeTicketCheckerHeadless>
                    )}
                  </ReactQueryFetcher>
                  <ReactQueryFetcher
                    reactQuery={useContentMyTicketQuery}
                    variables={{seriesId, includeWaitfree: true}}
                    ignoreLoading={true}
                    ignoreFetching={true}
                  />
                </>
              )}
            </OnlyInLoginChecker>
          </ContentSaleStateCheckerHeadless>
        </div>
        <div className="flex-1 overflow-hidden rounded-b-12pxr bg-theme-bg-surface-alt px-24pxr pt-8pxr">
          <ContentMainTicketActionContainer
            seriesId={seriesId}
            tabType={tabType}
            className="mb-8pxr"
          />
          <ContentHomeTabInfoContainer seriesId={seriesId} />
        </div>
      </div>
      <div className="flex flex-col overflow-hidden mb-28pxr ml-4px w-632pxr rounded-12pxr">
        <MainTab
          isMobile={false}
          tabItems={tabList.map(tab => ({
            ...tab,
            hasDot: tab.hasNewIcon,
          }))}
          currentTabItemId={tabType}
          className="my-0 bg-theme-bg-surface-alt px-15pxr pb-12pxr pt-28pxr"
        />
        {PcContentHomeTabComponentMap[tabType](seriesId)}
      </div>
    </div>
  );
}

const PcContentHomeTabComponentMap: {
  [key in ContentHomeTabType]: (seriesId: number) => ReactNode;
} = {
  [ContentHomeTabType.overview]: seriesId => (
    <ContentHomeTabPcContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.preview]: seriesId => (
    <ContentHomeTabPcContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.product]: seriesId => (
    <ContentHomeTabPcContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.about]: seriesId => (
    <ContentDetailTabContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.notice]: seriesId => (
    <ContentNoticeTabPcContainer seriesId={seriesId} />
  ),
  [ContentHomeTabType.comment]: seriesId => (
    <ContentHomeTabPcContainer seriesId={seriesId} />
  ),
};
